/* eslint-disable indent */
import './style.scss';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useParams } from 'react-router-dom';

import { Button } from 'primereact/button';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import SendRemindMultipleProjectDialog from 'features/Council/components/SendRemindMultipleProjectDialog';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';

import Members from 'features/Council/components/Detail/Members';

import { downloadFile, getDetailCouncilWithMultipleProjects } from 'apis/councils.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';

import { COUNCIL_TYPE_CODE, TIMEZONE_VN } from 'constant';

import { formatCurrency, formatFileName, getRoles, checkIsAllowedCRUDCouncil } from 'utils/func';

export default function DetailCouncil() {
  // #region Data
  const roles = getRoles();
  const { t } = useTranslation();

  const { projectProposalId, councilId } = useParams();

  // Council
  const {
    data: councilData,
    isLoading,
    refetch: refetchCouncil,
  } = useQuery(['detailCouncil', councilId], () =>
    getDetailCouncilWithMultipleProjects({ councilId })
  );

  const council = useMemo(() => councilData?.data ?? null, [councilData]);
  const isEditAllowed = useMemo(
    () =>
      council?.projectProposals.every((pp) => checkIsAllowedCRUDCouncil(pp?.projectType, roles)),
    [council]
  );

  const isUpdateResultAllowed = useMemo(
    () =>
      council?.projectProposals?.map((pp) => {
        if (checkIsAllowedCRUDCouncil(pp?.projectType, roles)) return true;
        return false;
      }),
    [council]
  );

  // Organizations (to get organization name for every council member)
  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );
  // end Organizations

  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmSendRemind, setIsVisibleConfirmSendRemind] = useState(false);
  const {
    mutate: mutateDownloadFile,
    error: errorResDownloadFile,
    isError: isErrorResDownloadFile,
  } = useMutation(downloadFile);
  // #endregion

  // #region Events
  const handleDownloadFile = (path, id) => {
    mutateDownloadFile(
      { path, id },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
        onError: () => {},
      }
    );
  };

  // #endregion

  const renderName = (rowData) => (
    <Link to={`/projects-revise/${rowData?.reference}?tab=0`}>{rowData?.vietnameseName}</Link>
  );

  const formatProjectType = (value) => t(`projectTypes.${value.projectType}`);

  const formatIndex = (rowData, index) => <span>{index.rowIndex + 1}</span>;

  const renderResearchFields = (rowData) => (
    <div>
      {rowData?.projectProposalResearchFields.map((item, index) => (
        <p className="text-left m-0">
          ({index + 1}) {item.researchField.name}
        </p>
      ))}
    </div>
  );

  const renderDate = (rowData) => (
    <span>
      {rowData?.submittedAt &&
        new Date(rowData?.submittedAt).toLocaleDateString('en-GB', {
          timeZone: TIMEZONE_VN,
        })}
    </span>
  );

  const renderCurrency = (rowData) => <span>{formatCurrency(rowData?.totalExpenditure)}</span>;

  const renderAction = (rowData, index) => {
    let to = '';
    if (council?.councilType.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      to = `/projects-revise/${rowData?.reference}?tab=1`;
    } else if (council?.councilType.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      to = `/projects-revise/${rowData?.reference}?tab=3`;
    } else if (council?.councilType.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      to = `/projects-revise/${rowData?.reference}?tab=2`;
    }
    if (isUpdateResultAllowed[index?.rowIndex]) {
      return (
        <Link to={to} className={!council ? 'pointer-events-none' : ''}>
          <Button
            icon="pi pi-check-square"
            className="bg-teal-600 border-teal-600"
            tooltip={t('council.viewList.editResult')}
            tooltipOptions={{ position: 'left' }}
            disabled={!council}
          />
        </Link>
      );
    }
    return null;
  };
  if (isLoading) return <Loading />;

  return (
    <>
      {isErrorResDownloadFile && (
        <ErrorDialog
          title={t('council.edit.titleEditCouncilError')}
          isError={isErrorResDownloadFile}
          error={errorResDownloadFile}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <SendRemindMultipleProjectDialog
        visible={isVisibleConfirmSendRemind}
        setVisible={setIsVisibleConfirmSendRemind}
        refetch={refetchCouncil}
        council={council}
      />

      <div className="bg-white p-5">
        {isEditAllowed && (
          <div className="flex justify-content-end align-items-center">
            <div className="flex gap-2">
              <Can do="send_mail" on="council">
                <Button
                  icon="pi pi-bell"
                  severity="success"
                  onClick={() => {
                    setIsVisibleConfirmSendRemind(true);
                  }}
                  tooltip={`${t('council.viewList.sendRemindMember')}
                ${t('council.fields.numberSentMail')}: ${
                    council?.projectProposalCouncil?.numberSentMail ?? 0
                  }
                ${t('council.fields.lastSentMail')}: ${
                    council?.projectProposalCouncil?.lastSentMail
                      ? new Date(council?.projectProposalCouncil?.lastSentMail).toLocaleDateString(
                          'vi',
                          {
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            timeZone: TIMEZONE_VN,
                          }
                        )
                      : ''
                  }`}
                  tooltipOptions={{ position: 'left' }}
                  disabled={!council?.completedAt}
                />
              </Can>
              <Can do="update" on="council">
                {council && !council?.deletedAt && (
                  <Link to={`/councils/edit/${councilId}`}>
                    <Button
                      icon="pi pi-pencil"
                      severity="warning"
                      tooltip={t('council.viewList.editCouncil')}
                      tooltipOptions={{ position: 'left' }}
                    />
                  </Link>
                )}
              </Can>
            </div>
          </div>
        )}
        <div className="m-0 bg-white p-5 p-fluid detail-info-council">
          <div className="flex flex-column mb-5">
            <h3
              className="line-height-3 text-4xl text-center font-semibold"
              style={{ marginBottom: '0px', paddingBottom: '1px' }}
            >
              {isLoading
                ? t('council.view.councilType.council')
                : `HỘI ĐỒNG ${council?.councilType?.name.toUpperCase()}`}
            </h3>

            {council && !council?.completedAt && (
              <h4 className="text-red-500 text-center">({t('council.view.notDoneAndSentMail')})</h4>
            )}
            {council &&
              council?.completedAt &&
              council?.councilMembers?.some((item) => !item?.isSentMail) && (
                <h4 className="text-blue-600 text-center">({t('council.view.onlyDone')})</h4>
              )}
          </div>

          <div className="ml-0 sm:ml-0 md:ml-4 lg:ml-4">
            <div className="pb-2">
              {/* Council info */}
              <div className="text-lg mb-3">
                <h4 className="mb-3">A. THÔNG TIN HỘI ĐỒNG</h4>
                <div className="ml-4 mb-2 grid">
                  <div className="col">
                    <span className="font-semibold">
                      {t('council.fields.name.specializationCouncil')}:{' '}
                    </span>
                    <span className="w-full">{council?.name}</span>
                  </div>
                </div>
                <div className="ml-4 grid">
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.establishDate')}: </span>
                    <span>
                      {council?.timeCreated &&
                        new Date(council?.timeCreated).toLocaleDateString('en-GB', {
                          timeZone: TIMEZONE_VN,
                        })}
                    </span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">
                      {t('council.fields.decisionToEstablish')}:{' '}
                    </span>
                    {council?.projectProposalCouncil?.establishmentFileUrl && (
                      <span
                        className="p-link text-primary underline"
                        onClick={() =>
                          handleDownloadFile(
                            council.projectProposalCouncil.establishmentFileUrl,
                            council?.id
                          )
                        }
                      >
                        {t('council.view.decision')}
                      </span>
                    )}
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
                    {council?.projectProposalCouncil?.reviewDeadline &&
                      new Date(council?.projectProposalCouncil?.reviewDeadline).toLocaleDateString(
                        'en-GB',
                        { timeZone: TIMEZONE_VN }
                      )}
                  </div>
                </div>
                {/* Council members */}
                {true && (
                  <Can do="read_member" on="council">
                    <div>
                      <h4 className="mb-3">
                        B. {t('council.view.councilMemberLabel').toUpperCase()}
                      </h4>
                      <div className="ml-4">
                        {!isLoading && council?.councilMembers && (
                          <Members
                            council={council}
                            refetch={refetchCouncil}
                            members={council?.councilMembers}
                            councilType={council?.councilType}
                            organizations={organizations}
                            projectProposalId={projectProposalId}
                            councilId={council?.id}
                            reviewDeadline={council?.projectProposalCouncil?.reviewDeadline}
                            isShowReview={false}
                            isHasActionColumn
                            hasProjects
                            isShowStatus={false}
                          />
                        )}
                      </div>
                    </div>
                  </Can>
                )}

                <div>
                  <h4 className="mb-3">C. DANH SÁCH ĐỀ TÀI</h4>
                  <div className="ml-4">
                    <DataTable
                      value={council?.projectProposals}
                      tableStyle={{ minWidth: '50rem' }}
                      className="p-datatable-gridlines"
                    >
                      <Column
                        field="vietnameseName"
                        header="TT"
                        style={{ minWidth: '2rem', textAlign: 'center' }}
                        body={formatIndex}
                      />
                      <Column field="vietnameseName" header="Tên đề tài" body={renderName} />
                      <Column
                        header="Loại đề tài"
                        body={formatProjectType}
                        style={{ width: '2rem', textAlign: 'center' }}
                      />
                      <Column field="ownerContact.fullname" header="Chủ nhiệm" />
                      <Column field="category" header="Nhóm ngành" body={renderResearchFields} />
                      <Column field="researchType.name" header="Loại hình" />
                      <Column
                        field="totalExpenditure"
                        header="Tổng kinh phí"
                        style={{ textAlign: 'right' }}
                        body={renderCurrency}
                      />
                      <Column field="submittedAt" header="Ngày nộp" body={renderDate} />
                      {(isEditAllowed || isUpdateResultAllowed?.some((item) => item === true)) && (
                        <Column
                          header="Thao tác"
                          style={{ textAlign: 'center' }}
                          body={renderAction}
                        />
                      )}
                    </DataTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
